import { $, $$, el } from './dom.js';

/**
 * Splits a long module page into named compartments.
 *
 * The tabs are built from the page rather than declared twice: every
 * `[data-section]` panel carries its own label, so adding a panel adds a tab
 * and the two can never disagree.
 *
 * What this deliberately does **not** do is split the form. Every compartment
 * stays inside the same `<form>`, hidden rather than unmounted, so one save bar
 * still covers the whole module and switching tabs never loses a pending edit.
 * That is also why hiding uses a class and not `display` on the fields: a
 * detached input would stop being submitted.
 */

const STORAGE_PREFIX = 'bilbi.section.';

export function initSectionNav(): void {
  for (const container of $$<HTMLElement>('[data-sections]')) {
    mount(container);
  }
}

function mount(container: HTMLElement): void {
  const sections = $$<HTMLElement>('[data-section]', container);
  if (sections.length < 2) return;

  const key = STORAGE_PREFIX + (container.dataset.sections || 'page');
  const nav = el('nav', { class: 'section-nav', attrs: { role: 'tablist' } });

  const tabs = new Map<string, HTMLButtonElement>();

  for (const section of sections) {
    const name = section.dataset.section ?? '';
    const label = section.dataset.sectionLabel ?? name;

    const tab = el('button', {
      class: 'section-tab',
      attrs: { type: 'button', role: 'tab', 'aria-controls': panelId(container, name) },
    });
    tab.append(el('span', { text: label }));

    // A count only when the panel says how much it holds: an absent badge is
    // less noise than a badge reading "0" on a panel that counts nothing.
    const count = section.dataset.sectionCount;
    if (count) tab.append(el('span', { class: 'section-tab-count', text: count }));

    tab.addEventListener('click', () => {
      show(name);
    });

    tabs.set(name, tab);
    nav.append(tab);

    section.id = panelId(container, name);
    section.setAttribute('role', 'tabpanel');
  }

  container.prepend(nav);

  function show(name: string): void {
    for (const section of sections) {
      const current = section.dataset.section === name;
      section.classList.toggle('hidden', !current);
    }

    for (const [key_, tab] of tabs) {
      const current = key_ === name;
      tab.classList.toggle('is-active', current);
      tab.setAttribute('aria-selected', String(current));
      // Only the active tab is in the tab order, which is what a tablist is
      // supposed to do: arrow keys move within it, Tab leaves it.
      tab.tabIndex = current ? 0 : -1;
    }

    try {
      localStorage.setItem(key, name);
    } catch {
      // Private browsing, or storage disabled. The compartments still work;
      // they simply do not remember which one was open.
    }
  }

  nav.addEventListener('keydown', (event) => {
    const names = [...tabs.keys()];
    const index = names.findIndex((name) => tabs.get(name)?.classList.contains('is-active'));
    if (index < 0) return;

    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;

    event.preventDefault();
    const next = names[(index + step + names.length) % names.length];
    if (!next) return;

    show(next);
    tabs.get(next)?.focus();
  });

  show(initial(sections, key));
}

/**
 * Which compartment opens first.
 *
 * The address wins over the remembered choice: a link to `#rewards` has to land
 * on the rewards, whatever was open last time.
 */
function initial(sections: HTMLElement[], key: string): string {
  const first = sections[0]?.dataset.section ?? '';
  const names = new Set(sections.map((section) => section.dataset.section));

  const hash = window.location.hash.replace(/^#/, '');
  if (hash && names.has(hash)) return hash;

  try {
    const stored = localStorage.getItem(key);
    if (stored && names.has(stored)) return stored;
  } catch {
    // Storage unavailable: fall through to the first compartment.
  }

  return first;
}

function panelId(container: HTMLElement, name: string): string {
  return `${container.dataset.sections || 'page'}-${name}`;
}

/**
 * Reveals the compartment holding an element, then scrolls to it.
 *
 * The guided tour is what needed it: on the twelve pages split into tabs, a
 * step whose panel sits in a closed compartment was pointing at nothing, and
 * the highlight silently shifted onto whichever panel happened to be visible.
 *
 * It was written before it had a caller, deleted as dead code, and restored the
 * day the caller arrived — which is the argument for deleting dead code rather
 * than the argument against it: the version that came back is the one somebody
 * actually needs.
 */
export function revealSection(target: Element): void {
  const section = target.closest<HTMLElement>('[data-section]');
  if (!section) return;

  const container = section.closest<HTMLElement>('[data-sections]');
  const name = section.dataset.section;
  if (!container || !name) return;

  $$<HTMLButtonElement>('.section-tab', container)
    .find((tab) => tab.getAttribute('aria-controls') === section.id)
    ?.click();

  target.scrollIntoView({ block: 'center' });
}

/** The panel currently open in a container, for a test or a caller. */
export function activeSection(container: HTMLElement): string | null {
  return $('[data-section]:not(.hidden)', container)?.getAttribute('data-section') ?? null;
}
